/**
 * What the Displacement page shows for each tenant.
 *
 * Deliberately explicit rather than derived from client_product_profiles. The
 * page reports on a MARKET, not on what a tenant sells, so the useful category
 * list is sometimes wider than the tenant's own products: blackboard has no
 * marking product and unit4 no HCM product, but both want to read those
 * markets. Deriving the list from the profile would quietly drop them.
 *
 * `categories` is ordered — the first is the landing page.
 * `countries` are the optional market scopes; an empty list means global only.
 * Every country named here must exist in procurement_category_intel_country,
 * which is built for a fixed set of markets.
 */
export const DISPLACEMENT_TENANT_SCOPES = {
  // UK builder of education PCs. Markets UK + US: Tiago, 2026-10-27, "replace Ireland with the US in all tenants in
  // the Displacement Radar" (Ireland was here alongside the UK until then).
  verypc: {
    categories: ['IT Hardware (Education only)'],
    countries: ['United Kingdom', 'United States'],
  },
  // National device programmes. The three markets where it has the most IT
  // Hardware evidence: Italy 5,398 awards, United States 5,147, Colombia 5,113.
  jpik: {
    categories: ['IT Hardware (Education only)'],
    countries: ['Italy', 'United States', 'Colombia'],
  },
  // [2026-11-03] positivo — same lens as jpik; its own market leads with Brazil.
  positivo: {
    categories: ['IT Hardware (Education only)'],
    countries: ['Brazil', 'Colombia', 'United States'],
  },
  blackboard: {
    categories: [
      'Learning Management Systems (LMS)',
      'Marking / Grading Platforms',
      'Academic Integrity / Plagiarism Detection',
    ],
    countries: [],
  },
  unit4: {
    categories: ['Finance & ERP', 'Talent Management / HCM'],
    countries: [],
  },
  terminalfour: {
    categories: ['Website Development (Education only)'],
    countries: [],
  },
  constructor_tech: {
    categories: [
      'Learning Management Systems (LMS)',
      'Marking / Grading Platforms',
      'Online Exam Platforms',
      'Remote Proctoring',
    ],
    countries: [],
  },
  // [2026-10-29] bibliotheca — no market chips (Tiago: "remove UK and US from the displacement radar in
  // bibliotheca"); without an entry it inherited the UK + US default. Empty `categories` keeps the old behaviour of
  // reading whatever the Contract filter has selected.
  bibliotheca: {
    categories: [],
    countries: [],
  },
  // [2026-10-29] smowl — global only (Tiago: "change Displacement Radar for tenant: smowl so its only global");
  // without an entry it inherited the UK + US default. Empty `categories` reads the Contract filter's selection.
  smowl: {
    categories: [],
    countries: [],
  },
  // [2026-10-18] testwe — lens profile 44's three categories.
  testwe: {
    categories: [
      'Marking / Grading Platforms',
      'Online Exam Platforms',
      'Remote Proctoring',
    ],
    countries: [],
  },
};

/** Alias resolution mirrors the API: `constructor` is a reserved property name. */
export function resolveDisplacementScope(tenantId) {
  const t = String(tenantId || '').trim().toLowerCase();
  if (!t) return null;
  const key = t === 'constructor' ? 'constructor_tech' : t;
  return Object.prototype.hasOwnProperty.call(DISPLACEMENT_TENANT_SCOPES, key)
    ? DISPLACEMENT_TENANT_SCOPES[key]
    : null;
}
